import { useLayoutEffect, useMemo, useRef, useState } from "react";
import type { Discrepancy, ManuscriptToken } from "../api/types";
import { CATEGORY_LABEL, categoryOf, type FlagCategory } from "../lib/reportStore";
import { formatTime } from "../lib/format";
import { pagePicture } from "../lib/pageView";
import type { PaneFind } from "./FindMode";

/**
 * The manuscript panel's Original view: the uploaded PDF's own pages, as
 * pictures, with each flag ringed on its words in its category's colour.
 *
 * A third view rather than a page swapped into the text, so the Formatted
 * view's line fitting never runs here. Each page keeps the `.ms-page` /
 * `data-page` the text views use (the page number in the bar and keeping your
 * place across views read those), and each ring is a `mark[data-id]` like a
 * highlighted word, so selecting a flag in the list scrolls to it here too.
 * Clicking any word plays from it.
 */

type Box = [number, number, number, number, number];   // pdf page, x0, y0, x1, y1 (fractions)
type Rect = [number, number, number, number];

interface Props {
  pages: number[];                 // the pages the panel shows, numbered as it shows them
  pageOffset: number;              // shown = PDF page + offset
  projectId: string;
  sizes: [number, number][] | undefined;
  fp: string | undefined;
  boxes: Record<string, Box> | undefined;   // token index -> where it is (none on the Review page)
  tokens: ManuscriptToken[];
  discrepancies: Discrepancy[];
  categories?: Map<string, string>;
  selectedId: string | null;
  activeId: string | null;
  onSelect: (id: string) => void;
  onPlayAt?: (time: number) => void;
  flagsOn: boolean;
  zoom: number;
  find?: PaneFind;                 // Find mode: this file's hits, and the one you're on
}

const pdfPageOf = (shown: number, offset: number) => (shown - offset >= 1 ? shown - offset : shown);

function labelFor(d: Discrepancy, cat: string): string {
  if (cat === "misread") return d.heard_text ? `Misread · heard “${d.heard_text}”` : "Misread";
  if (cat === "missing") return "Missing";
  if (cat === "added") return d.heard_text ? `Added · “${d.heard_text}”` : "Added";
  if (cat === "manual") return d.heard_text || "Manual flag";
  return CATEGORY_LABEL[cat as FlagCategory] ?? cat;
}

/** A flag's words as one box per printed line. */
function linesOf(bs: Box[]): Rect[] {
  const out: Rect[] = [];
  for (const b of [...bs].sort((p, q) => p[2] - q[2] || p[1] - q[1])) {
    const last = out[out.length - 1];
    const h = b[4] - b[2];
    if (last && Math.abs(last[1] - b[2]) < h * 0.5) {
      out[out.length - 1] = [Math.min(last[0], b[1]), Math.min(last[1], b[2]), Math.max(last[2], b[3]), Math.max(last[3], b[4])];
    } else out.push([b[1], b[2], b[3], b[4]]);
  }
  return out;
}

export function OriginalPages(p: Props) {
  const { boxes, tokens, discrepancies, categories } = p;
  const [hover, setHover] = useState<{ page: number; idx: number } | null>(null);
  const frame = useRef(0);

  // Each flag's rings, by PDF page.
  const rings = useMemo(() => {
    const out = new Map<number, { id: string; cat: string; rects: Rect[]; label: string; caret: boolean }[]>();
    if (!boxes) return out;
    for (const d of discrepancies) {
      const cat = categories?.get(d.id) ?? categoryOf(d.type);
      const caret = d.type === "added" && typeof d.insert_before_index === "number";
      const ids = d.manuscript_token_indices?.length ? d.manuscript_token_indices
        : caret ? [d.insert_before_index as number] : [];
      const found = ids.map((i) => boxes[String(i)]).filter(Boolean) as Box[];
      if (!found.length) continue;
      const byPage = new Map<number, Box[]>();
      for (const b of found) byPage.set(b[0], [...(byPage.get(b[0]) ?? []), b]);
      for (const [pg, bs] of byPage) {
        const rects = caret ? [[bs[0][1] - 0.004, bs[0][2], bs[0][1] + 0.002, bs[0][4]] as Rect] : linesOf(bs);
        out.set(pg, [...(out.get(pg) ?? []), { id: d.id, cat, rects, label: labelFor(d, cat), caret }]);
      }
    }
    return out;
  }, [boxes, discrepancies, categories]);

  // Find mode's hits, by PDF page: boxed like a highlighter, the one you're on
  // ringed (the panel scrolls to [data-find-now]).
  const finds = useMemo(() => {
    const out = new Map<number, { id: string; rects: Rect[]; now: boolean }[]>();
    if (!boxes || !p.find) return out;
    for (const h of p.find.hits) {
      const found = h.tokens.map((i) => boxes[String(i)]).filter(Boolean) as Box[];
      const byPage = new Map<number, Box[]>();
      for (const b of found) byPage.set(b[0], [...(byPage.get(b[0]) ?? []), b]);
      for (const [pg, bs] of byPage) {
        out.set(pg, [...(out.get(pg) ?? []), { id: h.id, rects: linesOf(bs), now: h.id === p.find.current }]);
      }
    }
    return out;
  }, [boxes, p.find]);

  // The words on each PDF page, for "play from the word clicked".
  const words = useMemo(() => {
    const out = new Map<number, { idx: number; b: Box }[]>();
    if (!boxes) return out;
    for (const [k, b] of Object.entries(boxes)) out.set(b[0], [...(out.get(b[0]) ?? []), { idx: Number(k), b }]);
    return out;
  }, [boxes]);
  const { timeOf, textOf } = useMemo(() => {
    const timeOf = new Map<number, number>();
    const textOf = new Map<number, string>();
    for (const t of tokens) {
      if (t.audio_time != null) timeOf.set(t.global_index, t.audio_time);
      textOf.set(t.global_index, t.text);
    }
    return { timeOf, textOf };
  }, [tokens]);

  const wordAt = (pg: number, fx: number, fy: number) => {
    const list = words.get(pg);
    if (!list) return null;
    let best: { idx: number; b: Box } | null = null;
    let bestD = Infinity;
    for (const w of list) {
      const [, x0, y0, x1, y1] = w.b;
      const pad = (y1 - y0) * 0.35;
      if (fy < y0 - pad || fy > y1 + pad) continue;           // on the word's line
      const dx = fx < x0 ? x0 - fx : fx > x1 ? fx - x1 : 0;
      if (dx < bestD) { best = w; bestD = dx; }
    }
    return best && bestD < 0.02 ? best : null;
  };
  const at = (e: React.MouseEvent<HTMLDivElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    return { fx: (e.clientX - r.left) / r.width, fy: (e.clientY - r.top) / r.height };
  };

  // Pictures as sharp as the page is drawn - on this screen, at this zoom -
  // and no sharper: the server snaps the width to a few steps, so they cache.
  const firstPage = useRef<HTMLDivElement | null>(null);
  const [shownW, setShownW] = useState(0);
  useLayoutEffect(() => {
    const el = firstPage.current;
    if (!el) return;
    const measure = () => setShownW(el.getBoundingClientRect().width);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const width = Math.min(1800, Math.round((shownW || 820 * p.zoom) * (window.devicePixelRatio || 1)));
  const playable = !!p.onPlayAt && !!boxes;

  return (
    <>
      {p.pages.map((shown) => {
        const pg = pdfPageOf(shown, p.pageOffset);
        const size = p.sizes?.[pg - 1] ?? [595, 842];
        const here = p.flagsOn ? rings.get(pg) ?? [] : [];
        const hw = hover && hover.page === pg ? words.get(pg)?.find((w) => w.idx === hover.idx) : undefined;
        const hTime = hw ? timeOf.get(hw.idx) : undefined;
        return (
          <div key={shown} className="ms-page ms-orig" data-page={shown} ref={shown === p.pages[0] ? firstPage : undefined}
            style={{ aspectRatio: `${size[0]} / ${size[1]}` }}>
            {p.fp && shownW > 0 && (
              <img src={pagePicture(p.projectId, pg, width, p.fp)} alt={`Page ${shown} as uploaded`}
                loading="lazy" decoding="async" draggable={false} />
            )}
            <div
              className={`ms-orig-layer${playable ? " playable" : ""}`}
              onMouseMove={playable ? (e) => {
                const { fx, fy } = at(e);
                cancelAnimationFrame(frame.current);
                frame.current = requestAnimationFrame(() => {
                  const w = wordAt(pg, fx, fy);
                  setHover((h) => (w ? (h?.page === pg && h.idx === w.idx ? h : { page: pg, idx: w.idx }) : null));
                });
              } : undefined}
              onMouseLeave={() => setHover(null)}
              onClick={playable ? (e) => {
                const { fx, fy } = at(e);
                const w = wordAt(pg, fx, fy);
                const t = w ? timeOf.get(w.idx) : undefined;
                if (t != null) p.onPlayAt!(t);
              } : undefined}
            >
              {(finds.get(pg) ?? []).map((f) => f.rects.map((r, i) => (
                <span
                  key={`find-${f.id}-${i}`}
                  className={`orig-find${f.now ? " now" : ""}`}
                  data-find-now={f.now && i === 0 ? "" : undefined}
                  style={{ left: `${r[0] * 100}%`, top: `${r[1] * 100}%`, width: `${(r[2] - r[0]) * 100}%`, height: `${(r[3] - r[1]) * 100}%` }}
                />
              )))}
              {here.map((f) => f.rects.map((r, i) => (
                <mark
                  key={`${f.id}-${i}`}
                  data-id={i === 0 ? f.id : undefined}
                  className={`orig-ring m-${f.cat}${f.caret ? " caret" : ""}${f.id === p.selectedId ? " selected" : ""}${f.id === p.activeId ? " active" : ""}`}
                  style={{ left: `${r[0] * 100}%`, top: `${r[1] * 100}%`, width: `${(r[2] - r[0]) * 100}%`, height: `${(r[3] - r[1]) * 100}%` }}
                  title={f.label}
                  onClick={(e) => { e.stopPropagation(); p.onSelect(f.id); }}
                >
                  {i === 0 && f.id === p.selectedId && <span className="orig-label">{f.label}</span>}
                </mark>
              )))}
              {hw && (
                <>
                  <span className="orig-hover" style={{ left: `${hw.b[1] * 100}%`, top: `${hw.b[4] * 100}%`, width: `${(hw.b[3] - hw.b[1]) * 100}%` }} />
                  {hTime != null && (
                    <span className="orig-tip" style={{ left: `${hw.b[3] * 100}%`, top: `${hw.b[2] * 100}%` }}>
                      ▶ Play from “{(textOf.get(hw.idx) ?? "").replace(/^[^\w]+|[^\w]+$/g, "")}” · {formatTime(hTime)}
                    </span>
                  )}
                </>
              )}
            </div>
          </div>
        );
      })}
    </>
  );
}
